import styled, { css, keyframes } from "styled-components";

export const C = {
  blue: "#1B229F",
  navy: "#0B1066",
  ink: "#070A33",
  black: "#040404",
  text: "#141414",
  yellow: "#FFFF00",
  orange: "#FF5227",
  orangeHover: "#E6461F",
  sky: "#2EA9DE",
  light: "#6CCBEF",
  pale: "#BDE8F5",
  white: "#FFFFFF",
  graphite: "#1A1A1A",
  graphiteDark: "#0D0D0D",
};

export const F = {
  title: "Poppins, sans-serif",
  body: "'Noto Sans', sans-serif",
  num: "Anton, sans-serif",
  marker: "'Permanent Marker', cursive",
};

export const ring = (w, color) => `inset 0 0 0 ${w}px ${color}`;
export const CARD_RING = ring(1, "rgba(255,255,255,0.1)");
export const EASE = "cubic-bezier(0.2,0.7,0.2,1)";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

export const pulsing = css`
  animation: ${pulse} 1.6s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Container = styled.main`
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: clamp(28px, 4vw, 48px) clamp(16px, 4vw, 40px);
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.gap || 28}px;
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.gap || 28}px;
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  text-decoration: none;
  font-family: ${F.title};
  line-height: 1;
  transition: background 160ms, transform 240ms ${EASE}, filter 240ms;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid ${C.yellow};
    outline-offset: 2px;
  }
`;

// variant: orange | yellow | outline | outlineYellow
export const Button = styled.button`
  ${buttonBase}
  font-weight: ${(p) => (p.variant === "outline" ? 600 : 700)};
  font-size: ${(p) => p.size || 14}px;
  padding: ${(p) => p.pad || "13px 18px"};
  width: ${(p) => (p.block ? "100%" : "auto")};
  ${(p) =>
    ({
      orange: css`
        background: ${C.orange};
        color: ${C.white};
        &:hover:not(:disabled) {
          background: ${C.orangeHover};
        }
      `,
      yellow: css`
        background: ${C.yellow};
        color: ${C.blue};
      `,
      outline: css`
        background: transparent;
        color: ${C.white};
        box-shadow: ${ring(1.5, "rgba(255,255,255,0.45)")};
        &:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.08);
        }
      `,
      outlineYellow: css`
        background: transparent;
        color: ${C.yellow};
        box-shadow: ${ring(1.5, "rgba(255,255,0,0.6)")};
        border-radius: 20px;
      `,
    }[p.variant || "orange"])}
`;

export const LinkButton = Button.withComponent("a");

export const H1 = styled.h1`
  margin: 0;
  font: 700 clamp(30px, 3.6vw, 44px) / 1.08 ${F.title};
  letter-spacing: -0.04em;
  text-wrap: balance;
`;

export const H2 = styled.h2`
  margin: 0;
  font: 700 20px/1.2 ${F.title};
  letter-spacing: -0.02em;
`;

export const ScanLines = styled.div.attrs({ "aria-hidden": true })`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.035) 0 1px, transparent 1px 4px);
`;

export const InfoChip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  background: ${C.ink};
  box-shadow: ${CARD_RING};
  font-size: 14px;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.8);
  text-align: left;
  strong {
    color: ${C.white};
    font-weight: 700;
  }
`;
